
import path from 'path';
import fs from 'fs';
import { map, prop } from 'ramda';
import { tmp } from '@bb-cli/base';
import createManifest from './manifest';
import { pad0 } from './utils';
import { zipDir, zipFiles } from './zip';
import orderItems from './order-items';

/**
 * An object that describes a provisioned package.
 * @typedef {Object} ProvisionPackage
 * @property {string} filename The filename of the provisioned package.
 * @property {Array.<ProvisionItem>} cxpItems The CXP items in the package.
 */

/**
 * A object that describes a CXPItem that has been packaged.
 * @typedef {Object} ProvisionItem
 * @augments CXPItem
 * @property {string} zip The path to the zipped dist.
 */

/**
 * A object that describes an item that has been compiled.
 * @private
 * @typedef {Object} BuiltItem
 * @augments CXPItem
 * @property {string} distDir The path to the built code on the file system.
 */

/**
 * An object that describes a catalog item in CXP Manager.
 * @typedef {Object} CXPItem
 * @property {string} name The CXP item name.
 * @property {string} type The CXP item type.
 * @property {?string} version The CXP item version.
 * @property {string} srcDir The path to the item's source on the file system.
 */

/**
 * @private
 * @param {CXPItem} item
 * @param {int} index Position of file in array.
 */
const indexedFilename = (item, index) => `${pad0(index, 5)}_${item.name}.zip`;

/**
 * @private
 * @param {CXPItem|BuiltItem} item
 * @return {string}
 */
const itemDir = (item) => item.distDir || item.srcDir;

/**
 * @private
 * @param {CXPItem|BuiltItem} item test
 * @param {int} index
 * @return {Promise.<ProvisionItem>} saved filename
 */
const packageIndexedItem = (workdir, ignore) => (item, index) =>
  zipDir(itemDir(item), path.join(workdir, indexedFilename(item, index)), ignore)
    .then(zip => Object.assign({}, item, { zip }));

/**
 * @private
 * @param {string} workdir
 * @param {Array.<ProvisionItem>} items
 * @returns {string} the full path of the manifest file
 */
const createManifestFile = (workdir, items) => {
  const manifest = createManifest(items);
  const manifestJson = JSON.stringify(manifest, null, 2);
  const manifestFile = path.join(workdir, 'manifest.json');
  fs.writeFileSync(manifestFile, manifestJson);
  return manifestFile;
};

/**
 * Curried callback from zipZips
 * @private
 * @callback zipZipsCallback
 * @param {Array.<ProvisionItem>} zipNames
 * @returns {Promise.<string>}
 */

/**
 * @private
 * @param saveAs
 * @returns {zipZipsCallback}
 */
const zipZippedItems = (workdir, saveAs) => (items) => {
  const manifestFile = createManifestFile(workdir, items);
  const zipNames = map(prop('zip'), items);
  const files = zipNames.concat([manifestFile]);
  return zipFiles(files, saveAs);
};

/**
 * @private
 * @param {Array.<CXPItem|BuiltItem>} Array of items to package.
 * @param {string} saveAs Filename to save package as.
 * @param {Array.<String>} Node globs to exclude from item packages.
 * @returns {Promise.<ProvisionPackage>}
 */
const packageItems = (cxpItems, saveAs, ignore = []) =>
  tmp.dir().then(({ dir, cleanup }) => {
    const orderedItems = orderItems(cxpItems);
    return Promise.all(orderedItems.map(packageIndexedItem(dir, ignore)))
      .then(zipZippedItems(dir, saveAs))
      .then(filename => {
        cleanup();
        return { filename, cxpItems };
      });
  });

export default packageItems;
